import type { Round, RoundSummary } from '../api/types';
// A tabela de meses vive em `format/date.ts`. Havia uma cópia idêntica aqui, e duas cópias é uma
// a mais do que se pode manter em dia.
import { MONTHS } from '../format/date';
import { t } from '../i18n';

/**
 * The series behind the Estatísticas charts (canvas 1l and 1o).
 *
 * Everything here is pure: rounds in, plottable numbers out. That is deliberate — the whole
 * screen is derived data with no endpoint behind it, so the arithmetic is the part that can
 * be wrong without anyone noticing. The rendering is just SVG.
 *
 * Two rules the canvas states and this module enforces:
 *
 * 1. **A chart declares its minimum and stays silent below it.** "Com 2 voltas, uma linha de
 *    tendência mente — silêncio é mais honesto." The thresholds live here, next to the maths
 *    they guard, rather than being scattered through the JSX.
 * 2. **Nothing is invented to fill a gap.** A round with no putts recorded is dropped from the
 *    putts series, not counted as zero.
 */

export interface SeriesPoint {
  /** Round id — lets a chart point link back to the round it came from. */
  id: string;
  /** When the round finished (or started, if it never did). */
  at: string;
  value: number;
}

/** A differential point also knows whether it is one of the eight defining the index. */
export interface DifferentialPoint extends SeriesPoint {
  counts: boolean;
}

type Dated = { completedAt: string | null; startedAt: string };

function timeOf(r: Dated): number {
  return new Date(r.completedAt ?? r.startedAt).getTime();
}

/** Oldest first. Every chart on this screen reads left to right, in time. */
function chronological<T extends Dated>(rounds: T[]): T[] {
  return [...rounds].sort((a, b) => timeOf(a) - timeOf(b));
}

// ------------------------------------------------------------------ thresholds

/**
 * A factual series over time — the handicap actually recorded, the score actually shot.
 * Three is the floor at which a shape exists at all, and it is also where WHS starts
 * producing an index, so below it the handicap chart would have nothing to draw anyway.
 */
export const MIN_ROUNDS_FOR_SERIES = 3;

/**
 * The differentials chart is stricter, and the canvas says so in as many words: "Precisa de
 * pelo menos 5 voltas concluídas". It is the only chart that draws an *inferred* trend line
 * rather than plotting recorded values, and a trend through three points is decoration.
 */
export const MIN_ROUNDS_FOR_DIFFERENTIALS = 5;

// --------------------------------------------------------------------- series

/**
 * Handicap index as it stood at the start of each round.
 *
 * This is the reason the statistics screen has to fetch every round individually:
 * `playerHandicapAtStart` exists only on the detail DTO. It is null until WHS has enough
 * rounds, and those nulls are dropped rather than zero-filled — a player without an index
 * does not have an index of zero.
 */
export function handicapSeries(details: Round[]): SeriesPoint[] {
  return chronological(details)
    .filter((r) => r.playerHandicapAtStart != null)
    .map((r) => ({
      id: r.id,
      at: r.completedAt ?? r.startedAt,
      value: r.playerHandicapAtStart as number,
    }));
}

/**
 * Score differentials, each flagged with whether it is one of the ones WHS is currently
 * using. That flag is the entire point of the chart: the canvas's subtitle is "Os 8 menores
 * dos últimos 20 são exatamente os que definem seu índice", and marking them is how the
 * screen shows the index is not a black box.
 *
 * `counting` comes from the caller (`countingDifferentials`) so the set is computed once
 * against the *full* history — a filtered view must still mark the same rounds the server's
 * index actually used.
 */
export function differentialSeries(
  rounds: RoundSummary[],
  counting: Set<string>,
): DifferentialPoint[] {
  return chronological(rounds)
    .filter((r) => r.scoreDifferential != null)
    .map((r) => ({
      id: r.id,
      at: r.completedAt ?? r.startedAt,
      value: r.scoreDifferential as number,
      counts: counting.has(r.id),
    }));
}

/** Strokes over par, per round. */
export function toParSeries(rounds: RoundSummary[]): SeriesPoint[] {
  return chronological(rounds)
    .filter((r) => r.totalToPar != null)
    .map((r) => ({ id: r.id, at: r.completedAt ?? r.startedAt, value: r.totalToPar as number }));
}

/** Greens in regulation, as a percentage of every hole scored. */
export function girSeries(details: Round[]): SeriesPoint[] {
  return chronological(details).map((r) => ({
    id: r.id,
    at: r.completedAt ?? r.startedAt,
    value: r.stats.greensInRegulationPct,
  }));
}

/**
 * Fairways hit. The base is *not* the same as GIR's — the server excludes par 3s, which have
 * no fairway to hit. The canvas prints that caveat under the chart because two lines on one
 * axis invite the reader to assume a shared denominator.
 */
export function fairwaySeries(details: Round[]): SeriesPoint[] {
  return chronological(details).map((r) => ({
    id: r.id,
    at: r.completedAt ?? r.startedAt,
    value: r.stats.fairwayHitPct,
  }));
}

// -------------------------------------------------------------------- by par

export interface ParPerformance {
  par: number;
  /** Mean strokes over par on holes of this par. Positive is worse. */
  averageOverPar: number;
  /** How many holes went into the mean — the sample the claim rests on. */
  holes: number;
}

/**
 * Average score over par, split by hole type. Par 6 exists on a handful of courses, so the
 * pars are discovered from the data rather than hardcoded to 3/4/5.
 */
export function performanceByPar(details: Round[]): ParPerformance[] {
  const byPar = new Map<number, number[]>();

  for (const round of details) {
    for (const score of round.scores) {
      const list = byPar.get(score.par) ?? [];
      list.push(score.strokes - score.par);
      byPar.set(score.par, list);
    }
  }

  return [...byPar.entries()]
    .map(([par, overPar]) => ({
      par,
      averageOverPar: overPar.reduce((a, b) => a + b, 0) / overPar.length,
      holes: overPar.length,
    }))
    .sort((a, b) => a.par - b.par);
}

/** Below this a "you lose strokes on par 5" claim is one bad round talking. */
const MIN_HOLES_FOR_PAR_DIAGNOSIS = 12;

/**
 * The one par type that is genuinely costing the player, or nothing.
 *
 * The canvas writes this as "Diagnóstico direto: você perde no par 5 — o único acima de
 * +0,8." A diagnosis that fires on a 0,02 difference is not direct, it is noise dressed as
 * advice, so the worst category has to be clear of the next by a real margin before this
 * says anything at all.
 */
export function parDiagnosis(byPar: ParPerformance[]): ParPerformance | null {
  const eligible = byPar.filter((p) => p.holes >= MIN_HOLES_FOR_PAR_DIAGNOSIS);
  if (eligible.length < 2) return null;

  const sorted = [...eligible].sort((a, b) => b.averageOverPar - a.averageOverPar);
  const [worst, second] = sorted;
  if (!worst || !second) return null;

  // A quarter of a stroke per hole is roughly 1,5 strokes a round on par 4s — visible.
  if (worst.averageOverPar - second.averageOverPar < 0.25) return null;
  return worst;
}

// ----------------------------------------------------------- by hole insight

export interface HoleCost {
  holeNumber: number;
  averageOverPar: number;
}

/**
 * The two holes doing the most damage, and what share of the player's over-par score they
 * account for — the canvas's "Os buracos 3 e 11 custam +1,1 e +1,0".
 *
 * Returns null when the share is not worth reporting: if the total over par is zero or
 * negative there is no damage to attribute, and if the two worst holes are not carrying an
 * outsized share then naming them says nothing a scorecard does not already say.
 */
export function worstHoles(
  costs: HoleCost[],
  minShare = 0.12,
): { holes: HoleCost[]; share: number } | null {
  const positive = costs.filter((c) => c.averageOverPar > 0);
  if (positive.length < 4) return null;

  const total = positive.reduce((sum, c) => sum + c.averageOverPar, 0);
  if (total <= 0) return null;

  const worst = [...positive].sort((a, b) => b.averageOverPar - a.averageOverPar).slice(0, 2);
  const share = worst.reduce((sum, c) => sum + c.averageOverPar, 0) / total;

  return share >= minShare ? { holes: worst, share } : null;
}

// ---------------------------------------------------------------- regression

/**
 * Least-squares fit over evenly spaced points, for the differentials chart's dashed trend.
 *
 * Deliberately the plainest possible model. The canvas draws a straight dashed line and
 * nothing more; anything fancier would imply a confidence this data does not support.
 * Returns null when there is no spread to fit.
 */
export function trendLine(values: number[]): ((index: number) => number) | null {
  const n = values.length;
  if (n < 2) return null;

  const meanX = (n - 1) / 2;
  const meanY = values.reduce((a, b) => a + b, 0) / n;

  let num = 0;
  let den = 0;
  for (let i = 0; i < n; i++) {
    const dx = i - meanX;
    num += dx * ((values[i] as number) - meanY);
    den += dx * dx;
  }
  if (den === 0) return null;

  const slope = num / den;
  return (i: number) => meanY + slope * (i - meanX);
}

// -------------------------------------------------------------------- labels

/**
 * "fev a jul de 2026", ou "jul de 2026" para um mês só — o subtítulo do cabeçalho em 1l.
 * Atravessar o ano soletra os dois, porque "dez a fev de 2026" se lê de trás para frente.
 */
export function dateRangeLabel(rounds: RoundSummary[]): string | null {
  if (rounds.length === 0) return null;
  const sorted = chronological(rounds);
  const first = new Date(timeOf(sorted[0] as RoundSummary));
  const last = new Date(timeOf(sorted[sorted.length - 1] as RoundSummary));

  const fm = MONTHS[first.getMonth()] ?? '';
  const lm = MONTHS[last.getMonth()] ?? '';

  if (first.getFullYear() !== last.getFullYear()) {
    return t('format.range.acrossYears', {
      from: fm,
      fromYear: first.getFullYear(),
      to: lm,
      toYear: last.getFullYear(),
    });
  }
  if (first.getMonth() === last.getMonth()) {
    return t('format.range.sameMonth', { month: lm, year: last.getFullYear() });
  }
  return t('format.range.sameYear', { from: fm, to: lm, year: last.getFullYear() });
}

/**
 * Up to four evenly spaced month labels under a chart's x-axis, as the canvas draws them.
 * Fewer points than labels means repeating a month, so the list is deduplicated in place —
 * "fev fev fev jul" would suggest four measurements where there are two.
 */
export function axisMonths(points: SeriesPoint[], slots = 4): string[] {
  if (points.length === 0) return [];
  const picks: string[] = [];
  for (let i = 0; i < slots; i++) {
    const at = points[Math.round((i / (slots - 1)) * (points.length - 1))];
    if (!at) continue;
    const label = MONTHS[new Date(at.at).getMonth()] ?? '';
    if (label !== picks[picks.length - 1]) picks.push(label);
  }
  return picks;
}
